import React, { useEffect, useState } from 'react';
import { User, Building2, Phone, Mail, Briefcase, Edit, Check, Trash2, Receipt } from 'lucide-react';
import { ContactService } from '../services/contactService';
import { ClientService } from '../services/clientService';
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button, ConfirmDialog, Toggle } from './ui';

const ContactDetailsModal = ({ contact, isOpen, onClose, initialEditMode = false, onContactUpdated, zIndex }) => {
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    clientId: '',
    billing: false,
    notes: ''
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const roleOptions = [
    { value: 'Project Management', label: 'Project Management' },
    { value: 'Owner/President', label: 'Owner/President' },
    { value: 'Accounts Payable', label: 'Accounts Payable' },
    { value: 'Admin', label: 'Admin' },
    { value: 'Field Staff', label: 'Field Staff' },
    { value: 'Estimator', label: 'Estimator' },
  ];

  // Initialize form data when contact changes
  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || contact.phoneNumber || '',
        role: contact.role || '',
        clientId: contact.client_id || contact.clientId || '',
        billing: contact.billing || false,
        notes: contact.notes || ''
      });
    }
  }, [contact]);

  // Load clients when in edit mode
  useEffect(() => {
    const loadClientsOnEditMode = async () => {
      if (isEditMode && clients.length === 0) {
        try {
          const data = await ClientService.getAllClients();
          // Convert to dropdown format
          const clientOptions = data.map((client) => ({
            value: client.id,
            label: client.name,
          }));
          setClients(clientOptions);
        } catch (error) {
          console.error('Error loading clients:', error);
        }
      }
    };

    loadClientsOnEditMode();
  }, [isEditMode, clients.length]);

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setErrors({});
    // Reset form data to original contact data
    if (contact) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || contact.phoneNumber || '',
        role: contact.role || '',
        clientId: contact.client_id || contact.clientId || '',
        billing: contact.billing || false,
        notes: contact.notes || ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.clientId) {
      newErrors.clientId = 'Client is required';
    }

    if (!formData.role.trim()) {
      newErrors.role = 'Role is required';
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.phone.trim()) {
      const cleaned = formData.phone.replace(/\D/g, '');
      if (cleaned.length !== 10) {
        newErrors.phone = 'Phone number must be 10 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    console.log('ContactDetailsModal: Save button clicked');
    console.log('ContactDetailsModal: Current formData:', formData);
    
    if (!validateForm()) {
      console.log('ContactDetailsModal: Validation failed, errors:', errors);
      return;
    }

    console.log('ContactDetailsModal: Validation passed, proceeding with save');
    setLoading(true);
    try {
      const updatedContact = await ContactService.updateContact(contact.id, {
        name: formData.name,
        email: formData.email || null,
        phoneNumber: formData.phone || null,
        role: formData.role,
        clientId: formData.clientId === '' || formData.clientId == null ? null : Number(formData.clientId),
        billing: formData.billing || false,
        notes: formData.notes || null
      });

      console.log('ContactDetailsModal: Setting edit mode to false');
      setIsEditMode(false);
      setErrors({});
      
      console.log('ContactDetailsModal: Calling onContactUpdated callback with:', updatedContact);
      if (onContactUpdated) {
        onContactUpdated(updatedContact);
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      setErrors({ submit: 'Failed to update contact. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: undefined }));
    }
  };

  const handleDelete = () => {
    if (!contact?.id) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!contact?.id) return;

    setLoading(true);
    try {
      await ContactService.deleteContact(contact.id);

      // Close both modals and trigger refresh
      setShowDeleteConfirm(false);
      if (onContactUpdated) {
        onContactUpdated(null); // Signal that contact was deleted
      }
      onClose();
    } catch (error) {
      console.error('Error deleting contact:', error);
      setErrors({ submit: 'Failed to delete contact. Please try again.' });
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.value === clientId);
    return client ? client.label : contact?.clientName || 'Not specified';
  };

  if (!contact) return null;

  return (
    <>
    <Modal trackAs="ContactDetailsModal" isOpen={isOpen} onClose={onClose} size="lg" zIndex={zIndex}>
      {/* Custom Header with Avatar and Title */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <User size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isEditMode ? formData.name || 'Unknown Contact' : contact.name || 'Unknown Contact'}
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isEditMode
                ? `${formData.role || 'Role'} at ${getClientName(formData.clientId)}`
                : contact.role && contact.clientName
                ? `${contact.role} at ${contact.clientName}`
                : contact.role || contact.clientName || 'Contact Details'}
            </p>
          </div>
        </div>
      </div>

      <ModalBody>
        {/* Error message */}
        {errors.submit && (
          <div
            style={{
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--danger)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {errors.submit}
          </div>
        )}

        {/* Two column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
          {/* Left Column: Contact Information */}
          <div>
            <h2
              style={{
                margin: 0,
                marginBottom: 'var(--space-4)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-200)',
              }}
            >
              Contact Information
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {/* Name */}
              {isEditMode ? (
                <Input
                  label="Full Name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter contact name"
                  error={errors.name}
                  required
                  icon={<User size={16} />}
                />
              ) : (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <User size={14} />
                    Full Name
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                    {contact.name || 'Not provided'}
                  </div>
                </div>
              )}

              {/* Email */}
              {(contact.email || isEditMode) && (
                isEditMode ? (
                  <Input
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contact@example.com"
                    error={errors.email}
                    icon={<Mail size={16} />}
                  />
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <Mail size={14} />
                      Email Address
                    </label>
                    <a
                      href={`mailto:${contact.email}`}
                      style={{ color: 'var(--cta-blue)', fontSize: 'var(--text-base)', textDecoration: 'none', fontWeight: 'var(--font-medium)' }}
                      onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                      onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                    >
                      {contact.email}
                    </a>
                  </div>
                )
              )}

              {/* Phone */}
              {(contact.phone || contact.phoneNumber || isEditMode) && (
                isEditMode ? (
                  <Input
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="(123) 456-7890"
                    error={errors.phone}
                    icon={<Phone size={16} />}
                  />
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <Phone size={14} />
                      Phone Number
                    </label>
                    <a
                      href={`tel:${contact.phone || contact.phoneNumber}`}
                      style={{ color: 'var(--cta-blue)', fontSize: 'var(--text-base)', textDecoration: 'none', fontWeight: 'var(--font-medium)' }}
                      onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                      onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                    >
                      {contact.phone || contact.phoneNumber}
                    </a>
                  </div>
                )
              )}

              {/* Role */}
              {isEditMode ? (
                <Dropdown
                  label="Role / Position"
                  options={roleOptions}
                  value={formData.role}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, role: value }));
                    if (errors.role) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.role;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select a role"
                  error={errors.role}
                />
              ) : (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Briefcase size={14} />
                    Role / Position
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                    {contact.role || 'Not provided'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Organization */}
          <div>
            <h2
              style={{
                margin: 0,
                marginBottom: 'var(--space-4)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-200)',
              }}
            >
              Organization
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {/* Client */}
              {isEditMode ? (
                <Dropdown
                  label="Client Organization"
                  options={clients}
                  value={formData.clientId}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, clientId: value }));
                    if (errors.clientId) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.clientId;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select a client"
                  searchable
                  error={errors.clientId}
                  loading={clients.length === 0}
                />
              ) : (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Building2 size={14} />
                    Client Organization
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                    {contact.clientName || 'Not specified'}
                  </div>
                </div>
              )}

              {/* Billing Contact Toggle */}
              {isEditMode ? (
                <Toggle
                  label="Billing Contact"
                  checked={formData.billing}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, billing: e.target.checked }));
                  }}
                  helperText="Enable if this contact handles billing and invoices"
                />
              ) : (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Receipt size={14} />
                    Billing Contact
                  </label>
                  <div style={{ color: contact.billing ? 'var(--success)' : 'var(--gray-400)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                    {contact.billing ? 'Yes' : 'No'}
                  </div>
                </div>
              )}

              {/* Notes */}
              {(contact.notes || isEditMode) && (
                isEditMode ? (
                  <div>
                    <label
                      htmlFor="notes"
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        color: 'var(--gray-300)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                      }}
                    >
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Additional notes about this contact"
                      style={{
                        width: '100%',
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-600)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        transition: 'border-color var(--transition-fast)',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--cta-blue)';
                        e.target.style.outline = 'none';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--gray-600)';
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Notes
                    </label>
                    <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                      {contact.notes || 'No notes'}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </ModalBody>

      {/* Sticky Footer with Actions */}
      <ModalFooter>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {isEditMode ? (
            <>
              {/* Left: Delete Button */}
              <Button variant="danger" size="md" onClick={handleDelete} disabled={loading} icon={<Trash2 size={16} />}>
                Delete Contact
              </Button>

              {/* Right: Cancel & Save */}
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <Button variant="ghost" size="md" onClick={handleCancelEdit} disabled={loading}>
                  Cancel
                </Button>
                <Button variant="primary" size="md" onClick={handleSave} loading={loading} icon={<Check size={16} />}>
                  Save Changes
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Empty left side for balance */}
              <div></div>

              {/* Right: Edit Button */}
              <Button variant="secondary" size="md" onClick={handleEdit} icon={<Edit size={16} />}>
                Edit Contact
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>

    {/* Delete Confirmation Dialog */}
    <ConfirmDialog
      isOpen={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirm={confirmDelete}
      title="Delete Contact"
      message={`Are you sure you want to delete ${contact.name}? This action cannot be undone and will permanently remove this contact from the system.`}
      confirmText="Delete Contact"
      cancelText="Cancel"
      variant="danger"
      loading={loading}
    />
    </>
  );
};

export default ContactDetailsModal;